import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Medal, Printer } from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { CategoryStandingsSheet } from './CategoryStandingsSheet';
import { CategoryResultsFilter, ALL_CATEGORIES } from './CategoryResultsFilter';
import { useCompetitionResults } from './useCompetitionResults';
import { useCategoryParam, withCategory } from './useCategoryParam';

interface CompetitionResultsProps {
  competitionSlug: string;
}

/**
 * A concluded stage's results: every class's standings, one under the other,
 * read the way a class's own standings page reads them
 * (`CategoryStandingsSheet`) but as results — the room wants who won U13F,
 * not which turn they climbed in.
 *
 * One under the other rather than a tab per class: a youth stage runs nine
 * classes, and their tabs do not fit a phone's width. A pick at the top
 * narrows the list to one class, for whoever only came for theirs — kept in the
 * URL (`?category=`), so a link or a reload lands on it, and carried to the
 * print page. Not a card
 * of its own: the tables are cards already, and a phone has no width to spare
 * for a second border around them.
 *
 * The printable sheet is a page of its own (`/competitions/:slug/results/print`);
 * printing this one leaves the controls off. Nothing is drawn when no class
 * climbed here, or the list cannot be read.
 */
export function CompetitionResults({ competitionSlug }: CompetitionResultsProps) {
  const { t } = useTranslation();
  const { categories, resultsOf } = useCompetitionResults(competitionSlug);
  const [filter, setFilter] = useCategoryParam(categories);

  if (categories.length === 0) return null;

  const shown = filter === ALL_CATEGORIES ? categories : categories.filter(({ id }) => id === filter);
  const printPath = withCategory(`/competitions/${competitionSlug}/results/print`, filter);

  return (
    <section className="space-y-4" data-testid="competition-results">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-2xl font-semibold">
          <Medal className="h-5 w-5" aria-hidden />
          {t('competitionStandings.results')}
        </h2>
        <Button asChild variant="outline" size="sm" className="gap-2 print:hidden">
          <Link to={printPath} data-testid="competition-results-print">
            <Printer className="size-4" aria-hidden />
            {t('competitionStandings.print')}
          </Link>
        </Button>
      </div>

      {categories.length > 1 && (
        <CategoryResultsFilter categories={categories} value={filter} onChange={setFilter} className="print:hidden" />
      )}

      <div className="space-y-10">
        {shown.map((category) => {
          const current = resultsOf(category.id);
          return (
            <section key={category.id} className="space-y-3" data-testid={`competition-results-${category.id}`}>
              {categories.length > 1 && <h3 className="text-lg font-semibold">{category.name}</h3>}
              {current.state === 'loading' && (
                <div className="space-y-3" data-testid="competition-results-loading">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              )}
              {current.state === 'not_closed' && (
                <p className="py-6 text-center text-muted-foreground">{t('competitionStandings.notClosed')}</p>
              )}
              {current.state === 'failed' && (
                <p className="py-6 text-center text-muted-foreground">{t('errors.loadFailed')}</p>
              )}
              {current.state === 'ready' && <CategoryStandingsSheet standings={current.standings} results />}
            </section>
          );
        })}
      </div>
    </section>
  );
}

export default CompetitionResults;
